import React, {useEffect, useState} from 'react';
import {Button, Col, Container, Row} from 'react-bootstrap';
import axios from 'axios';
import {useNavigate} from 'react-router-dom';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';
import '../AdminDashboard.css';
import './Pricing.css';
import {subscribeToPlan, upgradeToPlan} from '../utils/subscribeToPlan';
import {capture, planEventName} from '../utils/analytics';
// Plans render through the SAME card the Billing tab, the account-setup wizard
// and the Cognitive Flows catalogue use — one renderer, one look, one price.
import PlanCard from '../components/billing/PlanCard';

// The app-wide primary navy (.sxi_primary_blue_btn) — keeps this page on the
// same accent as the rest of the dashboard.
const PRICING_ACCENT = '#132848';

const Pricing = ({ViewedPage, app_url}) => {
    const navigate = useNavigate();
    const [plans, setPlans] = useState([]);
    const [masterFeatures, setMasterFeatures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [activeSubscription, setActiveSubscription] = useState(null);
    const user_email = Cookies.get('user_email');
    const shop_domain = Cookies.get('shop');

    const normalizeId = (value) => {
        if (!value) return null;
        if (typeof value === 'string') return value;
        if (typeof value === 'object' && value.$oid) return value.$oid;
        if (typeof value?.toString === 'function') return value.toString();
        return null;
    };

    const isCurrentPlan = (plan) => {
        if (!activeSubscription || !plan) return false;
        const activePlanId = normalizeId(activeSubscription?.plan?._id || activeSubscription?.plan_id);
        const cardPlanId = normalizeId(plan?._id);
        if (activePlanId && cardPlanId) return activePlanId === cardPlanId;
        const activePlanName = String(activeSubscription?.plan_name || activeSubscription?.plan?.name || '').toLowerCase();
        return activePlanName && activePlanName === String(plan?.name || '').toLowerCase();
    };

    const getDisplayPriceForPlan = (plan) => {
        if (isCurrentPlan(plan) && Number.isFinite(Number(activeSubscription?.plan_price))) {
            return Number(activeSubscription.plan_price);
        }
        return Number(plan?.price || 0);
    };

    const sortedPlans = [...plans].sort((a, b) => {
        const priceDiff = getDisplayPriceForPlan(a) - getDisplayPriceForPlan(b);
        if (priceDiff !== 0) return priceDiff;
        return String(a?.name || '').localeCompare(String(b?.name || ''));
    });

    useEffect(() => {
        ViewedPage(102);
        fetchPlans();
        fetchCurrentSubscription();
        fetchMasterFeatures();
    }, [ViewedPage]);

    const fetchPlans = async () => {
        try {
            const params = shop_domain ? `?shop_domain=${shop_domain}` : '';
            const response = await axios.get(`${app_url}/api/subscriptions/plans${params}`, {
                withCredentials: true
            });
            if (response.data.success) {
                setPlans(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching plans:', error);
            toast.error('Failed to load pricing plans');
        } finally {
            setLoading(false);
        }
    };

    const fetchCurrentSubscription = async () => {
        try {
            const params = shop_domain ? `?shop_domain=${shop_domain}&lite=1` : '?lite=1';
            const response = await axios.get(`${app_url}/api/subscriptions/my-subscription${params}`, {
                withCredentials: true
            });
            if (response.data.success) {
                setActiveSubscription(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching active subscription', err);
        }
    };

    const fetchMasterFeatures = async () => {
        try {
            const response = await axios.get(`${app_url}/api/subscriptions/master-features`, {
                withCredentials: true
            });
            if (response.data.success) {
                setMasterFeatures(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching master features:', error);
        }
    };

    // ================== UPGRADE FLOW ==================
    const handleUpgrade = async (plan) => {
        if (!user_email) {
            toast.error('Please login to subscribe');
            navigate('/login');
            return;
        }
        if (!shop_domain) {
            toast.error('Shop information missing. Please refresh the page or log in again.');
            return;
        }

        setSelectedPlan(plan);
        setProcessing(true);
        capture(planEventName(plan, 'clicked'), {
            plan_id: normalizeId(plan._id),
            plan_name: plan.name,
            price: plan.price,
            currency: plan.currency,
            surface: 'pricing_page',
            action: 'upgrade',
        });

        const result = await upgradeToPlan({
            plan,
            appUrl: app_url,
            userEmail: user_email,
            shopDomain: shop_domain,
            notify: {
                success: (m) => toast.success(m),
                error: (m) => toast.error(m),
                info: (m) => toast(m, {icon: 'ℹ️', duration: 4000}),
            },
        });

        setProcessing(false);

        if (result.status === 'activated') {
            capture(planEventName(plan, 'subscribed'), {
                plan_id: normalizeId(plan._id),
                plan_name: plan.name,
                surface: 'pricing_page',
                action: 'upgrade',
                is_free: Number(plan.price) === 0,
            });
            toast.success(result.message || 'Subscription upgraded successfully!');
            navigate('/subscription-management');
            return;
        }
        if (result.status === 'cancelled') {
            toast.error('Upgrade cancelled');
            return;
        }
        toast.error(result.message || 'Failed to upgrade subscription');
    };
    // =================================================

    // Run ₹1 card verification once; card is saved via webhook and refunded. Used for free plan and first-time paid.
    // ================== NEW SUBSCRIPTION FLOW ==================
    // The mechanics moved to utils/subscribeToPlan.js (2026-08-21) so the
    // account-setup wizard's plan step runs the identical payment path — free
    // plans need a ₹1 card authorisation BEFORE /create, and a second
    // hand-written copy is how that gets dropped. Upgrades stay here: /create
    // refuses a shop that already has an active subscription.
    const handleSubscribe = async (plan) => {
        if (processing) return;
        const currentShop = Cookies.get('shop');
        if (!currentShop) {
            toast.error('No shop selected. Please log in or select a shop.');
            return;
        }
        if (activeSubscription && !isCurrentPlan(plan)) {
            handleUpgrade(plan);
            return;
        }

        if (!user_email) {
            toast.error('Please login to subscribe');
            navigate('/login');
            return;
        }

        setSelectedPlan(plan);
        setProcessing(true);
        capture(planEventName(plan, 'clicked'), {
            plan_id: normalizeId(plan._id),
            plan_name: plan.name,
            price: plan.price,
            currency: plan.currency,
            surface: 'pricing_page',
            action: 'subscribe',
        });

        const result = await subscribeToPlan({
            plan,
            appUrl: app_url,
            userEmail: user_email,
            shopDomain: shop_domain,
            notify: {
                success: (m) => toast.success(m),
                error: (m) => toast.error(m),
                info: (m) => toast(m, {icon: 'ℹ️', duration: 4000}),
            },
        });

        if (result.status === 'has_active_subscription') {
            handleUpgrade(plan);
            return;
        }

        setProcessing(false);

        if (result.status === 'activated') {
            // Captured before the redirects below — a full navigation would lose a
            // queued event, and posthog-js flushes queued events on unload.
            capture(planEventName(plan, 'subscribed'), {
                plan_id: normalizeId(plan._id),
                plan_name: plan.name,
                surface: 'pricing_page',
                action: 'subscribe',
                is_free: result.isFree === true,
            });
            if (result.isFree) {
                toast.success('Free plan activated! Features are now available in your account.');
                if (result.subscription) setActiveSubscription(result.subscription);
                // Full navigation so the app re-reads the refreshed cookie.
                window.location.href = '/subscription-management';
                return;
            }
            toast.success('Subscription activated successfully!');
            navigate('/subscription-management');
            return;
        }

        if (result.status === 'cancelled') {
            toast.error('Payment cancelled. You can choose a plan again.');
            return;
        }

        toast.error(result.message || 'Failed to create subscription');
    };
    // ========================================================

    if (loading) {
        return (
            <div className="main-container subscription-management-container pricing-page-container">
                <div className="admin-loading-inline">
                    <div className="admin-loading-content">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-2">Loading pricing plans...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="main-container subscription-management-container pricing-page-container">
            <div className="admin-header opportunities-header">
                <div className="d-flex justify-content-between align-items-center w-100">
                    <div className="main-title">
                        <h2>Choose Your Plan</h2>
                    </div>
                    <Button
                        className="back-button btn-height"
                        onClick={() => navigate('/settings?tab=billing-payment')}
                    >
                        Back
                    </Button>
                </div>
                <p className="admin-subtitle">
                    Select the perfect plan for your business needs
                </p>
            </div>

            <Container fluid className="admin-content" style={{gutterX: '0px'}}>
                <Row>
                    <Col>
                        {plans.length === 0 ? (
                            <div className="admin-section">
                                <div className="text-center py-5">
                                    <p className="admin-subtitle">No pricing plans available at the moment.</p>
                                </div>
                            </div>
                        ) : (
                            <Row className="g-4 pricing-plan-grid">
                                {sortedPlans.map((plan) => {
                                    const currentPlan = isCurrentPlan(plan);
                                    const displayPrice = getDisplayPriceForPlan(plan);
                                    // Adapt the legacy subscription_plans row into the shared
                                    // PlanCard shape (chargeMinorUnits + curated highlights).
                                    const cardPlan = {
                                        id: normalizeId(plan._id),
                                        name: plan.name,
                                        description: plan.description || '',
                                        currency: plan.currency || 'INR',
                                        interval: plan.interval || 'monthly',
                                        chargeMinorUnits: Math.round(displayPrice * 100),
                                        isPopular: plan.name.toLowerCase().includes('pro'),
                                        isCurrent: currentPlan,
                                        highlights: (plan.masterFeatures || [])
                                            .map((featureId) => masterFeatures.find((f) => normalizeId(f._id) === normalizeId(featureId))?.name)
                                            .filter(Boolean),
                                    };
                                    return (
                                        <Col key={plan._id} xs={12} md={6} lg={4}>
                                            <PlanCard
                                                plan={cardPlan}
                                                accent={PRICING_ACCENT}
                                                busy={processing && selectedPlan?._id === plan._id}
                                                ctaLabel={activeSubscription ? 'Upgrade' : 'Subscribe Now'}
                                                onSelect={() => handleSubscribe(plan)}
                                            />
                                        </Col>
                                    )
                                })}
                            </Row>
                        )}
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default Pricing;

// import React, { useState, useEffect } from 'react';
// import { Container, Row, Col, Card, Button, Badge, Spinner } from 'react-bootstrap';
// import axios from 'axios';
// import { useNavigate } from 'react-router-dom';
// import Cookies from 'js-cookie';
// import toast from 'react-hot-toast';
// import '../AdminDashboard.css';
// import './Pricing.css';

// const Pricing = ({ ViewedPage, app_url }) => {
//   const navigate = useNavigate();
//   const [plans, setPlans] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [selectedPlan, setSelectedPlan] = useState(null);
//   const [processing, setProcessing] = useState(false);
//   const [activeSubscription, setActiveSubscription] = useState(null);
//   const user_email = Cookies.get('user_email');
//   const shop_domain = Cookies.get('shop');

//   useEffect(() => {
//     ViewedPage(102);
//     fetchPlans();
//     fetchCurrentSubscription();
//   }, [ViewedPage]);

//   const fetchPlans = async () => {
//     try {
//       const shop_domain = Cookies.get('shop');
//       const params = shop_domain ? `?shop_domain=${shop_domain}` : '';
//       const response = await axios.get(`${app_url}/api/subscriptions/plans${params}`, {
//         withCredentials: true
//       });
//       if (response.data.success) {
//         setPlans(response.data.data || []);
//       }
//     } catch (error) {
//       console.error('Error fetching plans:', error);
//       toast.error('Failed to load pricing plans');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const fetchCurrentSubscription = async () => {
//     try {
//       const params = shop_domain ? `?shop_domain=${shop_domain}` : '';
//       const response = await axios.get(`${app_url}/api/subscriptions/my-subscription${params}`, {
//         withCredentials: true
//       });
//       if (response.data.success) {
//         setActiveSubscription(response.data.data);
//       }
//     } catch (err) {
//       console.error('Error fetching active subscription', err);
//     }
//   };

//   const handleSubscribe = async (plan) => {
//     // if there's already an active subscription for this shop and
//     // the user is trying to pick a different plan, send them to
//     // subscription management instead of creating a new one.
//     if (activeSubscription && activeSubscription.plan && activeSubscription.plan._id !== plan._id) {
//       navigate('/subscription-management');
//       return;
//     }

//     if (!user_email) {
//       toast.error('Please login to subscribe');
//       navigate('/login');
//       return;
//     }

//     const shop_domain = Cookies.get('shop');
//     if (!shop_domain) {
//       toast.error('Shop information missing. Please refresh the page or log in again.');
//       return;
//     }

//     setSelectedPlan(plan);
//     setProcessing(true);

//     try {
//       // free plan: ensure card is verified by charging ₹1 and refunding
//       if (plan.price === 0) {
//         const authResp = await axios.post(`${app_url}/api/subscriptions/payment-methods/create-auth-order`, {
//           shop_domain: shop_domain
//         }, { withCredentials: true });

//         if (!authResp.data.success) {
//           throw new Error('Failed to create auth order');
//         }

//         const { order_id, amount: authAmount, currency: authCurrency, razorpay_key_id } = authResp.data.data;
//         await new Promise((resolve, reject) => {
//           const script = document.createElement('script');
//           script.src = 'https://checkout.razorpay.com/v1/checkout.js';
//           script.onload = () => {
//             const options = {
//               key: razorpay_key_id,
//               order_id,
//               name: 'ScaleX AI',
//               description: 'Card verification charge ₹1',
//               prefill: { email: user_email },
//               theme: { color: '#3399cc' },
//               handler: async function(res) {
//                 // refund immediately
//                 try {
//                   await axios.post(`${app_url}/api/subscriptions/payment-methods/refund`, { payment_id: res.razorpay_payment_id }, { withCredentials: true });
//                 } catch (refundErr) {
//                   console.error('refund failed', refundErr);
//                 }
//                 resolve();
//               },
//               modal: {
//                 ondismiss: function() {
//                   reject(new Error('Auth payment cancelled'));
//                 }
//               }
//             };
//             const razorpay = new window.Razorpay(options);
//             razorpay.open();
//           };
//           document.body.appendChild(script);
//         });
//       }

//       const response = await axios.post(`${app_url}/api/subscriptions/create`, {
//         plan_id: plan._id,
//         customer_email: user_email,
//         shop_domain: shop_domain
//       }, {
//         withCredentials: true
//       });

//       if (response.data.success) {
//         const { razorpay_subscription_id, razorpay_key_id, amount, currency, is_free_plan } = response.data.data;

//         // sanity check: server should signal free plan when plan.price === 0
//         if (plan.price === 0 && !is_free_plan) {
//           console.warn('Expected free plan flag but server returned paid subscription', {
//             plan,
//             response: response.data.data
//           });
//         }

//         // ===== free-plan special case =====
//         // Razorpay's hosted subscription page displays a ₹5 amount for plans
//         // priced at ₹1 (this is a limitation on their side and no ₹5 charge
//         // is actually captured).  To avoid confusing users we skip the
//         // subscription checkout entirely for a free plan and simply create the
//         // subscription record on the server.  The earlier ₹1 auth/refund has
//         // already verified the card; if the user wants to save a card we can
//         // ask them from the subscription management screen at any later time.
//         // In rare cases the server response may omit the `is_free_plan` flag;
//         // treat any 0‑price plan as free so the user isn't forced through the
//         // paid checkout.
//         if (is_free_plan || plan.price === 0) {
//           toast.success('Free plan activated! Please add a payment method under subscription management to save your card for future upgrades.');
//           // set local active subscription so the plan is highlighted immediately
//           if (response.data.data.subscription) {
//             setActiveSubscription(response.data.data.subscription);
//           }
//           navigate('/subscription-management', {
//             state: {
//               promptAddPayment: true,
//               subscription: response.data.data.subscription
//             }
//           });
//           setProcessing(false);
//           return;
//         }

//         // ===== paid plan flow =====
//         // if there is no subscription id (unlikely) just finish
//         if (!razorpay_subscription_id) {
//           toast.success('Subscription activated successfully!');
//           navigate('/subscription-management');
//           setProcessing(false);
//           return;
//         }

//         // Load Razorpay script for subscription (paid plan)
//         const script = document.createElement('script');
//         script.src = 'https://checkout.razorpay.com/v1/checkout.js';
//         script.onload = () => {
//           const options = {
//             key: razorpay_key_id,
//             subscription_id: razorpay_subscription_id,
//             name: 'ScaleX AI',
//             description: plan.name,
//             image: '/logo.png',
//             prefill: {
//               email: user_email,
//             },
//             theme: {
//               color: '#3399cc'
//             },
//             handler: function (response) {
//               toast.success('Subscription activated successfully!');
//               navigate('/subscription-management');
//             },
//             modal: {
//               ondismiss: function () {
//                 setProcessing(false);
//                 toast.error('Payment cancelled');
//               }
//             }
//           };

//           const razorpay = new window.Razorpay(options);
//           razorpay.open();
//         };
//         document.body.appendChild(script);
//       } else {
//         toast.error(response.data.message || 'Failed to create subscription');
//         setProcessing(false);
//       }
//     } catch (error) {
//       console.error('Error creating subscription:', error);
//       const msg = error.response?.data?.message;
//       if (msg && msg.toLowerCase().includes('active subscription')) {
//         // redirect user to management page instead of showing error
//         navigate('/subscription-management');
//       } else {
//         toast.error(msg || 'Failed to create subscription');
//       }
//     } finally {
//       setProcessing(false);
//     }
//   };

//   if (loading) {
//     return (
//       <div className="main-container subscription-management-container pricing-page-container">
//         <div className="admin-loading-inline">
//           <div className="admin-loading-content">
//             <div className="spinner-border text-primary" role="status">
//               <span className="visually-hidden">Loading...</span>
//             </div>
//             <p className="mt-2">Loading pricing plans...</p>
//           </div>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="main-container subscription-management-container pricing-page-container">
//       <div className="admin-header opportunities-header">
//         <div className="main-title">
//           <h2>Choose Your Plan</h2>
//         </div>
//         <p className="admin-subtitle">
//           Select the perfect plan for your business needs
//         </p>
//       </div>

//       <Container fluid className="admin-content" style={{gutterX: '0px'}}>

//         <Row>
//           <Col>
//             {plans.length === 0 ? (
//               <div className="admin-section">
//                 <div className="text-center py-5">
//                   <p className="admin-subtitle">No pricing plans available at the moment.</p>
//                 </div>
//               </div>
//             ) : (
//               <Row className="g-4">
//                 {plans.map((plan) => (
//                   <Col key={plan._id} xs={12} md={6} lg={4}>
//                     <div className={`admin-section pricing-plan-card ${plan.name.toLowerCase().includes('pro') ? 'featured-plan' : ''} ${activeSubscription && activeSubscription.plan && activeSubscription.plan._id === plan._id ? 'active-plan' : ''}`}>
//                       {plan.name.toLowerCase().includes('pro') && (
//                         <div className="featured-badge">
//                           <Badge bg="primary">Most Popular</Badge>
//                         </div>
//                       )}
//                       {activeSubscription && activeSubscription.plan && activeSubscription.plan._id === plan._id && (
//                         <div className="active-plan-badge">
//                           <Badge bg="success">Active</Badge>
//                         </div>
//                       )}
//                       <div className="admin-section-header">
//                         <h3 className="admin-section-title">{plan.name}</h3>
//                       </div>

//                       <div className="pricing-plan-content">
//                         <div className="pricing-price-wrapper text-center">
//                           <div className="pricing-price">
//                             <span className="currency">{plan.currency === 'INR' ? '₹' : '$'}</span>
//                             <span className="amount">{plan.price.toLocaleString()}</span>
//                             {plan.price!=0 && (
//                               <span className="period">/ {plan.interval === 'monthly' ? 'mo' : 'yr' } </span>
//                             )}
//                           </div>
//                           {plan.description && (
//                             <p className="admin-section-description mt-2">{plan.description}</p>
//                           )}
//                         </div>

//                         {plan.features && plan.features.length > 0 && (
//                           <div className="pricing-features">
//                             <ul className="pricing-features-list">
//                               {plan.features.map((feature, index) => (
//                                 <li key={index}>
//                                   <span className="feature-check">✓</span>
//                                   <span className="feature-text">{feature}</span>
//                                 </li>
//                               ))}
//                             </ul>
//                           </div>
//                         )}

//                         <div className="pricing-action">
//                           <Button
//                             variant={plan.name.toLowerCase().includes('pro') ? 'primary' : 'outline-primary'}
//                             className="w-100 admin-btn"
//                             onClick={() => handleSubscribe(plan)}
//                             disabled={processing || (activeSubscription && activeSubscription.plan && activeSubscription.plan._id === plan._id)}
//                           >
//                             {processing && selectedPlan?._id === plan._id ? (
//                               <>
//                                 <Spinner animation="border" size="sm" className="me-2" />
//                                 Processing...
//                               </>
//                             ) : activeSubscription && activeSubscription.plan && activeSubscription.plan._id === plan._id ? (
//                               'Current Plan'
//                             ) : (
//                               'Subscribe Now'
//                             )}
//                           </Button>
//                         </div>
//                       </div>
//                     </div>
//                   </Col>
//                 ))}
//               </Row>
//             )}
//           </Col>
//         </Row>
//       </Container>
//     </div>
//   );
// };

// export default Pricing;
